import { create } from 'zustand';
import { supabase, reportSupabaseError } from '@/lib/supabase';
import type { StudyStreak, StudyStats, TopicPerformance } from '@/types';

interface StatsState {
  streak: StudyStreak | null;
  weeklyStats: StudyStats | null;
  topicPerformance: TopicPerformance[];
  isLoading: boolean;
  error: string | null;
  setStreak: (streak: StudyStreak) => void;
  setWeeklyStats: (stats: StudyStats) => void;
  fetchStreak: (userId: string) => Promise<void>;
  fetchWeeklyStats: (userId: string) => Promise<void>;
  fetchTopicPerformance: (userId: string) => Promise<void>;
  recordStudySession: (userId: string, cardsReviewed: number, correctCount: number, minutes: number) => Promise<void>;
}

export const useStatsStore = create<StatsState>((set, get) => ({
  streak: null,
  weeklyStats: null,
  topicPerformance: [],
  isLoading: false,
  error: null,
  setStreak: (streak) => set({ streak }),
  setWeeklyStats: (weeklyStats) => set({ weeklyStats }),

  fetchStreak: async (userId: string) => {
    const { data, error } = await supabase
      .from('study_streaks')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (error) {
      reportSupabaseError(error, 'fetchStreak');
      return;
    }

    if (data) {
      // Calculate weekly activity from study_sessions
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);

      const { data: sessions } = await supabase
        .from('study_sessions')
        .select('created_at')
        .eq('user_id', userId)
        .gte('created_at', weekAgo.toISOString());

      const weeklyActivity = Array(7).fill(false);
      sessions?.forEach((session) => {
        const dayIndex = Math.floor(
          (new Date().getTime() - new Date(session.created_at).getTime()) / (1000 * 60 * 60 * 24)
        );
        if (dayIndex < 7) {
          weeklyActivity[6 - dayIndex] = true;
        }
      });

      set({
        streak: {
          current_streak: data.current_streak,
          longest_streak: data.longest_streak,
          last_study_date: data.last_study_date,
          weekly_activity: weeklyActivity,
        },
      });
    } else {
      // Initialize streak for new user
      set({
        streak: {
          current_streak: 0,
          longest_streak: 0,
          last_study_date: '',
          weekly_activity: Array(7).fill(false),
        },
      });
    }
  },

  fetchWeeklyStats: async (userId: string) => {
    set({ isLoading: true });
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);

    const { data, error } = await supabase
      .from('study_sessions')
      .select('cards_reviewed, correct_count, duration_seconds')
      .eq('user_id', userId)
      .gte('created_at', weekAgo.toISOString());

    if (error) {
      reportSupabaseError(error, 'fetchWeeklyStats');
      set({ error: error.message, isLoading: false });
      return;
    }

    const totals = (data || []).reduce(
      (acc, session) => ({
        cards: acc.cards + (session.cards_reviewed || 0),
        correct: acc.correct + (session.correct_count || 0),
        seconds: acc.seconds + (session.duration_seconds || 0),
      }),
      { cards: 0, correct: 0, seconds: 0 }
    );

    set({
      weeklyStats: {
        cards_reviewed: totals.cards,
        accuracy: totals.cards > 0 ? Math.round((totals.correct / totals.cards) * 100) : 0,
        study_time_minutes: Math.round(totals.seconds / 60),
        period: 'week',
      },
      isLoading: false,
    });
  },

  fetchTopicPerformance: async (userId: string) => {
    // Fetch all flashcards with their document_id
    const { data: flashcards, error: flashcardsError } = await supabase
      .from('flashcards')
      .select('document_id, interval_days, ease_factor')
      .eq('user_id', userId);

    if (flashcardsError) {
      reportSupabaseError(flashcardsError, 'fetchTopicPerformance');
    }

    if (!flashcards || flashcards.length === 0) {
      set({ topicPerformance: [] });
      return;
    }

    // Fetch documents for titles
    const { data: documents } = await supabase
      .from('documents')
      .select('id, title')
      .eq('user_id', userId);

    const docMap = new Map(documents?.map((d) => [d.id, d.title]) || []);

    // Group flashcards by document_id
    const grouped: Record<string, typeof flashcards> = {};
    flashcards.forEach((card) => {
      if (!card.document_id) return;
      if (!grouped[card.document_id]) {
        grouped[card.document_id] = [];
      }
      grouped[card.document_id].push(card);
    });

    // Calculate performance per document
    const performance: TopicPerformance[] = Object.entries(grouped).map(([docId, cards]) => {
      const total = cards.length;
      // Cards with interval >= 21 days are considered "mastered"
      const mastered = cards.filter((c) => c.interval_days >= 21).length;
      const correctPercentage = total > 0 ? Math.round((mastered / total) * 100) : 0;

      let strength: 'strong' | 'moderate' | 'weak';
      if (correctPercentage >= 80) {
        strength = 'strong';
      } else if (correctPercentage >= 50) {
        strength = 'moderate';
      } else {
        strength = 'weak';
      }

      return {
        topic: docMap.get(docId) || 'Unknown',
        document_id: docId,
        document_title: docMap.get(docId) || 'Unknown',
        questions_attempted: total,
        correct_percentage: correctPercentage,
        strength,
      };
    });

    // Sort by performance (weak first for attention)
    performance.sort((a, b) => a.correct_percentage - b.correct_percentage);

    set({ topicPerformance: performance });
  },

  recordStudySession: async (userId: string, cardsReviewed: number, correctCount: number, minutes: number) => {
    const today = new Date().toISOString().split('T')[0];

    // Record session (DB stores seconds)
    const { error: sessionError } = await supabase.from('study_sessions').insert({
      user_id: userId,
      session_type: 'flashcards',
      cards_reviewed: cardsReviewed,
      correct_count: correctCount,
      duration_seconds: minutes * 60,
    });
    if (sessionError) {
      reportSupabaseError(sessionError, 'recordStudySession');
    }

    // Update streak
    const { data: streakData } = await supabase
      .from('study_streaks')
      .select('*')
      .eq('user_id', userId)
      .maybeSingle();

    if (streakData) {
      const lastDate = new Date(streakData.last_study_date);
      const todayDate = new Date(today);
      const daysDiff = Math.floor((todayDate.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24));

      let newStreak = streakData.current_streak;
      if (daysDiff === 1) {
        newStreak += 1;
      } else if (daysDiff > 1) {
        newStreak = 1;
      }

      await supabase
        .from('study_streaks')
        .update({
          current_streak: newStreak,
          longest_streak: Math.max(newStreak, streakData.longest_streak),
          last_study_date: today,
        })
        .eq('user_id', userId);
    } else {
      await supabase.from('study_streaks').insert({
        user_id: userId,
        current_streak: 1,
        longest_streak: 1,
        last_study_date: today,
      });
    }

    // Refetch stats
    await get().fetchStreak(userId);
    await get().fetchWeeklyStats(userId);
  },
}));
